import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { fmtMoneyFull, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { FilePdf, CheckCircle, PaperPlaneTilt, HandHeart, Bank } from "@phosphor-icons/react";

const Step = ({ n, title, done, active, children, testid }) => (
  <div className={`border rounded-sm p-3 ${done ? "border-emerald-500/40 bg-emerald-500/5" : active ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-[#111114]"}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <span className={`w-5 h-5 rounded-full grid place-items-center mono text-[10px] ${done ? "bg-emerald-500 text-black" : active ? "bg-amber-500 text-black" : "bg-zinc-800 text-zinc-400"}`}>
        {done ? "✓" : n}
      </span>
      <span className={`text-sm font-medium ${done ? "text-emerald-300" : active ? "text-amber-200" : "text-zinc-300"}`}>{title}</span>
    </div>
    <div className="pl-7 space-y-2">{children}</div>
  </div>
);

const Btn = ({ children, onClick, disabled, primary, testid }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className={`text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm flex items-center gap-2 disabled:opacity-40 transition-colors ${
      primary ? "bg-emerald-500 hover:bg-emerald-400 text-black font-semibold" : "border border-zinc-700 hover:border-zinc-500 text-zinc-200"
    }`}>{children}</button>
);

export default function ClosingPipeline({ lead, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [ebName, setEbName] = useState("");
  const [ebEmail, setEbEmail] = useState("");
  const [ebPrice, setEbPrice] = useState("");

  const loadDocs = async () => {
    try { const r = await api.get(`/leads/${lead.id}/documents`); setDocs(r.data); } catch {}
  };
  useEffect(() => { loadDocs(); /* eslint-disable-next-line */ }, [lead.id, lead.pa_sent_at, lead.assignment_sent_at]);

  const call = async (fn, okMsg, errMsg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(okMsg); await onUpdated?.(); await loadDocs(); return r; }
    catch (e) { toast.error(e?.response?.data?.detail || errMsg); }
    finally { setBusy(false); }
  };

  const genPA = () => call(() => api.post(`/leads/${lead.id}/purchase-agreement/generate`), "Purchase Agreement dispatched", "Failed");
  const markPASigned = () => call(() => api.post(`/leads/${lead.id}/purchase-agreement/mark-signed`, { signed: true }), "PA marked signed", "Failed");
  const setEndBuyer = () => {
    if (!ebName || !ebEmail || !ebPrice) { toast.error("Fill name, email, price"); return; }
    call(() => api.post(`/leads/${lead.id}/end-buyer`, { name: ebName, email: ebEmail, offer_price: Number(ebPrice) }), "End buyer set — Assignment dispatched", "Failed");
    setEbName(""); setEbEmail(""); setEbPrice("");
  };
  const markAssignSigned = () => call(() => api.post(`/leads/${lead.id}/assignment/mark-signed`, { signed: true }), "Assignment signed → Title package auto-dispatched", "Failed");
  const manualHandoff = () => call(() => api.post(`/leads/${lead.id}/title-handoff`), "Title package sent", "Failed");

  const downloadUrl = (docId) => {
    const t = localStorage.getItem("jr_token");
    return `${API}/documents/${docId}/download?token=${encodeURIComponent(t)}`;
  };

  const paDone = !!lead.pa_signed_at;
  const paSent = !!lead.pa_sent_at;
  const ebSet = !!(lead.end_buyer_email && lead.end_buyer_offer);
  const assignSent = !!lead.assignment_sent_at;
  const assignDone = !!lead.assignment_signed_at;
  const titleDone = !!lead.title_handoff_at;

  return (
    <div className="p-5 border-b border-zinc-800 space-y-3" data-testid="closing-pipeline">
      <div className="overline mb-2 flex items-center gap-2"><HandHeart size={12} /> Closing Pipeline · Lead-to-Close</div>

      <Step n={1} title="Purchase Agreement" done={paDone} active={!paDone && lead.stage === "hot_deals"} testid="step-pa">
        {paSent ? (
          <div className="text-[11px] mono text-zinc-500">Sent {fmtDate(lead.pa_sent_at)} to {lead.owner_email || 'seller'}</div>
        ) : (
          <div className="text-[11px] mono text-zinc-500">Auto-fires when seller replies YES</div>
        )}
        <div className="flex flex-wrap gap-2">
          <Btn onClick={genPA} disabled={busy} testid="btn-gen-pa"><FilePdf size={13} /> {paSent ? "Resend" : "Generate & Send"}</Btn>
          {paSent && !paDone && <Btn onClick={markPASigned} disabled={busy} primary testid="btn-mark-pa-signed"><CheckCircle size={13} weight="bold" /> Mark seller-signed</Btn>}
          {paDone && <span className="text-[11px] mono text-emerald-400">signed {fmtDate(lead.pa_signed_at)}</span>}
        </div>
      </Step>

      <Step n={2} title="End Buyer + Assignment" done={assignDone} active={paDone && !assignDone} testid="step-assignment">
        {!ebSet ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input value={ebName} onChange={e => setEbName(e.target.value)} placeholder="End buyer name" data-testid="eb-name"
              className="bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500" />
            <input value={ebEmail} onChange={e => setEbEmail(e.target.value)} placeholder="buyer@example.com" data-testid="eb-email"
              className="bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500" />
            <input value={ebPrice} onChange={e => setEbPrice(e.target.value)} placeholder="Their offer $" type="number" data-testid="eb-price"
              className="bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500" />
          </div>
        ) : (
          <div className="text-[11px] mono text-zinc-400">
            {lead.end_buyer_name} · {lead.end_buyer_email} · offer {fmtMoneyFull(lead.end_buyer_offer)}
            <span className="text-emerald-400"> · assignment fee {fmtMoneyFull((lead.end_buyer_offer || 0) - (lead.offer_amount || 0))}</span>
          </div>
        )}
        {assignSent && <div className="text-[11px] mono text-zinc-500">Assignment sent {fmtDate(lead.assignment_sent_at)}</div>}
        <div className="flex flex-wrap gap-2">
          {!ebSet && <Btn onClick={setEndBuyer} disabled={busy || !paDone} testid="btn-set-eb"><PaperPlaneTilt size={13} /> Set buyer & auto-send Assignment</Btn>}
          {assignSent && !assignDone && <Btn onClick={markAssignSigned} disabled={busy} primary testid="btn-mark-assign-signed"><CheckCircle size={13} weight="bold" /> Mark buyer-signed</Btn>}
          {assignDone && <span className="text-[11px] mono text-emerald-400">signed {fmtDate(lead.assignment_signed_at)}</span>}
        </div>
      </Step>

      <Step n={3} title="Title Handoff" done={titleDone} active={assignDone && !titleDone} testid="step-title">
        {titleDone ? (
          <div className="text-[11px] mono text-emerald-400">Settlement package emailed {fmtDate(lead.title_handoff_at)}</div>
        ) : (
          <div className="text-[11px] mono text-zinc-500">Auto-fires the moment both PA + Assignment are signed. Requires Title Company email + bank details in Settings.</div>
        )}
        <div className="flex flex-wrap gap-2">
          <Btn onClick={manualHandoff} disabled={busy || !assignDone} testid="btn-manual-handoff"><Bank size={13} /> Force title handoff</Btn>
        </div>
      </Step>

      {docs.length > 0 && (
        <div className="pt-1">
          <div className="overline mb-2">Documents ({docs.length})</div>
          <ul className="space-y-1.5">
            {docs.map(d => (
              <li key={d.id} className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium truncate">{d.filename}</div>
                  <div className="text-[10px] mono text-zinc-500">{d.kind} · {fmtDate(d.created_at)}</div>
                </div>
                <a href={downloadUrl(d.id)} target="_blank" rel="noreferrer"
                   className="text-[10px] mono uppercase tracking-[0.15em] px-2 py-1 rounded-sm border border-zinc-700 hover:border-zinc-500">
                  Download
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
